import { useState, useCallback, useEffect, useRef } from 'react';
import { useWorkSessionSSE } from './useWorkSessionSSE';
import { isSessionDead } from '../services/mobile/sessionLatch';
/**
 * Create platform-specific useWorkSession hook
 * This is the base implementation used by both web and mobile versions
 */
export function createUseWorkSession(workSessionServiceInstance, breakTrackingServiceInstance) {
    // Deduplicate concurrent getActiveSession calls (handles React StrictMode
    // double-mount). The resolved response is reused for 2s, so EVERY break
    // mutation must clear this: otherwise the next refresh replays the session
    // as it was BEFORE the break started or ended, undoing the status the
    // mutation just set — and isOnBreak reads that status.
    let activeSessionPromise = null;
    /**
     * useWorkSession Hook
     *
     * Manages work session (clock in/out) state with:
     * - Live elapsed time tracking
     * - Automatic session refresh
     * - Loading and error states
     * - Platform-specific API client support
     *
     * @param options Configuration options
     * @returns Work session state and actions
     *
     * @example
     * ```tsx
     * const { isClockedIn, elapsedMs, clockIn, clockOut, isLoading } = useWorkSession();
     *
     * const handleToggle = async () => {
     *   if (isClockedIn) {
     *     await clockOut();
     *   } else {
     *     await clockIn();
     *   }
     * };
     * ```
     */
    function useWorkSession(options = {}) {
        const { refreshInterval = 60000, // 1 minute
        enablePolling: enablePollingOption, autoInitialize = true, enableSSE = false, sseBaseUrl = '', sseAuthToken, sseGetAuthToken, offlineQueue, } = options;
        // When SSE is enabled, disable polling by default (unless explicitly enabled)
        const enablePolling = enablePollingOption ?? !enableSSE;
        // Core state
        const [state, setState] = useState({
            activeSession: null,
            isLoading: false,
            error: null,
            isInitialized: false,
            activeBreak: null,
        });
        /**
         * A check-in is already running somewhere other than the place this
         * client named. Not an error string: nothing failed for the person, they
         * are already on the clock. The UI names the place and the time.
         */
        const [openElsewhere, setOpenElsewhere] = useState(null);
        /**
         * The person belongs to no location at all, in an account that fences
         * check-ins. Only an administrator can resolve it.
         */
        const [noLocationAssigned, setNoLocationAssigned] = useState(false);
        // Offline punch queue: pending count drives the widget's pending-sync
        // state; when a replay drains the queue we refresh from the server.
        const [pendingPunches, setPendingPunches] = useState(() => offlineQueue?.pendingCount() ?? 0);
        // Elapsed time tracking (updates every second when clocked in and not on break)
        const [elapsedMs, setElapsedMs] = useState(0);
        const timerRef = useRef(null);
        // Guard against duplicate initialization (e.g. StrictMode double-mount or parent re-render remount)
        const initializingRef = useRef(false);
        /**
         * Calculate elapsed milliseconds from session start time
         */
        const calculateElapsed = useCallback((session) => {
            if (!session?.start_time)
                return 0;
            const startTime = new Date(session.start_time).getTime();
            return Math.max(0, Date.now() - startTime);
        }, []);
        /**
         * Start the elapsed time timer
         */
        const startTimer = useCallback((session) => {
            // Clear any existing timer
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            // Set initial elapsed time
            setElapsedMs(calculateElapsed(session));
            // Update every second
            timerRef.current = setInterval(() => {
                setElapsedMs(calculateElapsed(session));
            }, 1000);
        }, [calculateElapsed]);
        /**
         * Stop the elapsed time timer
         */
        const stopTimer = useCallback(() => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setElapsedMs(0);
        }, []);
        /**
         * The break the user is currently on, read back from the server.
         *
         * Break state used to live ONLY in this hook's memory: `activeBreak` was
         * set by startBreak and by nothing else. So a user who took a break and
         * then logged in again — new tab, reopened app, another device — came back
         * with activeBreak null: the widget said they were working, and endBreak
         * refused ("no active break to end") because it had no break id. The
         * server knew all along (the session carries status 'on_break'), the
         * client just never asked.
         */
        const fetchOpenBreak = useCallback(async (sessionId) => {
            try {
                const breaks = await breakTrackingServiceInstance.getBreaksBySession(sessionId);
                if (!breaks.success || !breaks.data)
                    return null;
                // status is authoritative; end_time covers a row whose status was not
                // written for any reason.
                return breaks.data.find((b) => b.status === 'active' && !b.end_time) || null;
            }
            catch {
                return null;
            }
        }, []);
        /**
         * Refresh the current session from the server
         */
        const refresh = useCallback(async () => {
            try {
                if (!activeSessionPromise) {
                    activeSessionPromise = workSessionServiceInstance.getActiveSession();
                    // Keep resolved promise cached briefly so sequential calls during
                    // StrictMode remount or component re-mount reuse the same result
                    activeSessionPromise.finally(() => {
                        setTimeout(() => {
                            activeSessionPromise = null;
                        }, 2000);
                    });
                }
                const response = await activeSessionPromise;
                if (response.success) {
                    const session = response.data || null;
                    // Rehydrate the break alongside the session. The extra request only
                    // happens while the server says the session is on break, so the
                    // polling path is unchanged for everyone who is simply working.
                    // Refreshing also CLEARS a stale break: one ended on another device
                    // leaves the session back in 'working', and this is what notices.
                    const onBreak = session?.status === 'on_break';
                    const activeBreak = onBreak && session ? await fetchOpenBreak(session.id) : null;
                    setState((prev) => ({
                        ...prev,
                        activeSession: session,
                        activeBreak,
                        isInitialized: true,
                        error: null,
                    }));
                    // The elapsed timer counts working time, so it stays paused while on
                    // break — the same thing startBreak does.
                    if (session && !onBreak) {
                        startTimer(session);
                    }
                    else {
                        stopTimer();
                    }
                }
                else {
                    setState((prev) => ({
                        ...prev,
                        isInitialized: true,
                    }));
                }
            }
            catch (error) {
                setState((prev) => ({
                    ...prev,
                    isInitialized: true,
                }));
            }
        }, [startTimer, stopTimer, fetchOpenBreak]);
        /**
         * Clock in - start a new work session
         * If a session already exists (409 conflict), automatically reconnects to it
         */
        const clockIn = useCallback(async (request) => {
            setState((prev) => ({ ...prev, isLoading: true, error: null }));
            // A fresh attempt supersedes whatever the last one asked about,
            // including the retry that answers either question.
            setOpenElsewhere(null);
            setNoLocationAssigned(false);
            try {
                const response = await workSessionServiceInstance.startSession(request);
                if (response.success && response.data) {
                    setState((prev) => ({
                        ...prev,
                        activeSession: response.data,
                        isLoading: false,
                        error: null,
                    }));
                    startTimer(response.data);
                    return true;
                }
                else {
                    // Geofence refusals. The server matches GPS against the fences
                    // of EVERY location this person is assigned to and passes on the
                    // first hit, so a violation means they are at none of their own
                    // locations — not that they picked the wrong one.
                    // The machine code lives on errorBody.code; ApiClient exposes only
                    // the human sentence as response.error. Matching the text alone
                    // meant a real geofence refusal fell through to the raw server
                    // payload — the friendly wording below never appeared.
                    const errorText = typeof response.error === 'string' ? response.error : JSON.stringify(response.error);
                    const geofenceCode = response.errorBody?.code === 'GEOFENCE_VIOLATION' || response.errorBody?.code === 'GEOFENCE_GPS_REQUIRED'
                        ? response.errorBody.code
                        : errorText && errorText.includes('GEOFENCE_GPS_REQUIRED')
                            ? 'GEOFENCE_GPS_REQUIRED'
                            : errorText && errorText.includes('GEOFENCE_VIOLATION')
                                ? 'GEOFENCE_VIOLATION'
                                : null;
                    if (geofenceCode) {
                        // When the server found them at one of their shops in ANOTHER
                        // account it says which one and what to do (suggested_location);
                        // that sentence is worth more than our generic one.
                        const namedElsewhere = geofenceCode === 'GEOFENCE_VIOLATION' &&
                            !!response.errorBody?.suggested_location &&
                            typeof response.error === 'string';
                        setState((prev) => ({
                            ...prev,
                            error: namedElsewhere
                                ? response.error
                                : geofenceCode === 'GEOFENCE_GPS_REQUIRED'
                                    ? 'Please enable location services to check in.'
                                    : 'You need to be at one of your locations to check in.',
                            isLoading: false,
                        }));
                        return false;
                    }
                    // Location-context failures get plain wording instead of the raw
                    // server payload — the "strange error" reports were this path.
                    const bodyCode = response.errorBody?.code;
                    if (bodyCode === 'LOCATION_REQUIRED') {
                        // Rare now: the server places the punch itself from GPS. This
                        // survives only for someone assigned to NO location in an
                        // account that fences check-ins. There is nothing for them to
                        // pick — an administrator has to put them on a location.
                        setState((prev) => ({ ...prev, isLoading: false, error: null }));
                        setNoLocationAssigned(true);
                        return false;
                    }
                    if (bodyCode === 'PERIOD_LOCKED') {
                        setState((prev) => ({
                            ...prev,
                            isLoading: false,
                            error: 'That pay period has been approved and locked. Ask a manager to reopen it before changing time in it.',
                        }));
                        return false;
                    }
                    if (bodyCode === 'LOCATION_INVALID') {
                        setState((prev) => ({
                            ...prev,
                            isLoading: false,
                            error: 'Your assigned location has changed. Log out and back in, then check in again.',
                        }));
                        return false;
                    }
                    // Check if error indicates an existing session (409 conflict).
                    // The server sends the machine code OPEN_SESSION_EXISTS; the
                    // message substrings remain as a fallback for older servers.
                    const errorMsg = response.error?.toLowerCase() || '';
                    const isExistingSessionError = response.errorBody?.code === 'OPEN_SESSION_EXISTS' ||
                        errorMsg.includes('already exists') ||
                        errorMsg.includes('open session') ||
                        errorMsg.includes('409');
                    if (isExistingSessionError) {
                        /**
                         * A check-in is already running. ONE check-in covers the day
                         * and follows the person between locations, so there is no
                         * second punch to make and nothing to move — but the 409 must
                         * not be swallowed either.
                         *
                         * Same place, or nothing to compare: reconnecting IS the truth
                         * — a second tab, a reopened app, a double tap.
                         *
                         * Somewhere else: this tap started nothing, so it cannot report
                         * success. Tell them where and when they checked in; their time
                         * is already running and they carry on.
                         */
                        const existing = await workSessionServiceInstance.getActiveSession();
                        const openSession = (existing.success && existing.data) || null;
                        const wantedLocationId = request?.location_id;
                        const openLocationId = openSession?.location_id;
                        await refresh();
                        if (openSession && wantedLocationId && openLocationId && wantedLocationId !== openLocationId) {
                            setState((prev) => ({ ...prev, isLoading: false, error: null }));
                            setOpenElsewhere({ session: openSession, requestedLocationId: wantedLocationId });
                            return false; // this tap started nothing — say so, honestly
                        }
                        // Reconnected to the session they meant. refresh() rehydrates the
                        // session but never touches isLoading, so clear it here or the
                        // clock keeps spinning over a session it has already adopted.
                        setState((prev) => ({ ...prev, isLoading: false, error: null }));
                        return true;
                    }
                    // Transport failure (no HTTP status): queue the punch for replay
                    // instead of losing it.
                    if (offlineQueue && response.status === undefined) {
                        offlineQueue.enqueue('clock_in', (request || {}));
                        setPendingPunches(offlineQueue.pendingCount());
                        setState((prev) => ({ ...prev, isLoading: false, error: null }));
                        return true;
                    }
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: response.error || 'Failed to check in',
                    }));
                    return false;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to check in';
                // Check for geofence-specific error codes
                if (errorMessage.includes('GEOFENCE_VIOLATION') || errorMessage.includes('GEOFENCE_GPS_REQUIRED')) {
                    setState((prev) => ({
                        ...prev,
                        error: errorMessage.includes('GEOFENCE_GPS_REQUIRED')
                            ? 'Please enable location services to check in.'
                            : 'You need to be at one of your locations to check in.',
                        isLoading: false,
                    }));
                    return false;
                }
                // Check if error indicates an existing session (409 conflict)
                const errorMsg = errorMessage.toLowerCase();
                const isExistingSessionError = errorMsg.includes('already exists') || errorMsg.includes('open session') || errorMsg.includes('409');
                if (isExistingSessionError) {
                    // Automatically reconnect to the existing session
                    await refresh();
                    // Return true since user is now connected to their existing session
                    return true;
                }
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: errorMessage,
                }));
                return false;
            }
        }, [startTimer, refresh]);
        /**
         * Clock out - end the current work session
         */
        const clockOut = useCallback(async (request) => {
            setState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                const response = await workSessionServiceInstance.endSession(request);
                if (response.success) {
                    setState((prev) => ({
                        ...prev,
                        activeSession: null,
                        isLoading: false,
                        error: null,
                    }));
                    stopTimer();
                    return true;
                }
                else {
                    if (offlineQueue && response.status === undefined) {
                        offlineQueue.enqueue('clock_out', {});
                        setPendingPunches(offlineQueue.pendingCount());
                        // Treat the local session as ended: the queued punch carries
                        // the real punch time and will close it on replay.
                        setState((prev) => ({ ...prev, activeSession: null, isLoading: false, error: null }));
                        stopTimer();
                        return true;
                    }
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: response.error || 'Failed to check out',
                    }));
                    return false;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to check out';
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: errorMessage,
                }));
                return false;
            }
        }, [stopTimer]);
        /**
         * Clear any error state
         */
        const clearError = useCallback(() => {
            setState((prev) => ({ ...prev, error: null }));
        }, []);
        /**
         * Decline the move. The session that is already running is left exactly
         * as it is — declining must never be a way to lose time — and the screen
         * says so rather than silently going quiet.
         */
        const dismissOpenElsewhere = useCallback(() => {
            setOpenElsewhere(null);
        }, []);
        const dismissNoLocationAssigned = useCallback(() => {
            setNoLocationAssigned(false);
        }, []);
        /**
         * Start a break - pauses the current work session
         */
        const startBreak = useCallback(async (request) => {
            if (!state.activeSession) {
                setState((prev) => ({
                    ...prev,
                    error: 'No active session to take a break from',
                }));
                return false;
            }
            setState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                const response = await breakTrackingServiceInstance.startBreak(state.activeSession.id, request);
                if (response.success && response.data) {
                    setState((prev) => ({
                        ...prev,
                        activeSession: prev.activeSession ? { ...prev.activeSession, status: 'on_break' } : prev.activeSession,
                        activeBreak: response.data,
                        isLoading: false,
                        error: null,
                    }));
                    activeSessionPromise = null;
                    // Pause the timer while on break
                    stopTimer();
                    return true;
                }
                else {
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: response.error || 'Failed to start break',
                    }));
                    return false;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to start break';
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: errorMessage,
                }));
                return false;
            }
        }, [state.activeSession, stopTimer]);
        /**
         * End break - resumes the work session
         */
        const endBreak = useCallback(async () => {
            // Last-resort resolve: the refresh path rehydrates activeBreak, but if
            // this runs before that lands (or the earlier fetch failed) the break is
            // still endable — ask the server for the id rather than telling a user
            // who IS on a break that there is no break to end.
            const openBreak = state.activeBreak ?? (state.activeSession ? await fetchOpenBreak(state.activeSession.id) : null);
            if (!openBreak) {
                setState((prev) => ({
                    ...prev,
                    error: 'No active break to end',
                }));
                return false;
            }
            setState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                const response = await breakTrackingServiceInstance.endBreak(openBreak.id);
                if (response.success) {
                    // The session is working again — say so, or isOnBreak's status
                    // fallback keeps the widget stuck on "On Break".
                    setState((prev) => ({
                        ...prev,
                        activeSession: prev.activeSession ? { ...prev.activeSession, status: 'working' } : prev.activeSession,
                        activeBreak: null,
                        isLoading: false,
                        error: null,
                    }));
                    activeSessionPromise = null;
                    // Resume the timer
                    if (state.activeSession) {
                        startTimer(state.activeSession);
                    }
                    return true;
                }
                else {
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: response.error || 'Failed to end break',
                    }));
                    return false;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to end break';
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: errorMessage,
                }));
                return false;
            }
        }, [state.activeBreak, state.activeSession, startTimer, fetchOpenBreak]);
        /**
         * Cancel break - resumes without counting break time
         */
        const cancelBreak = useCallback(async () => {
            // Same last-resort resolve as endBreak.
            const openBreak = state.activeBreak ?? (state.activeSession ? await fetchOpenBreak(state.activeSession.id) : null);
            if (!openBreak) {
                setState((prev) => ({
                    ...prev,
                    error: 'No active break to cancel',
                }));
                return false;
            }
            setState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                const response = await breakTrackingServiceInstance.cancelBreak(openBreak.id);
                if (response.success) {
                    // The session is working again — say so, or isOnBreak's status
                    // fallback keeps the widget stuck on "On Break".
                    setState((prev) => ({
                        ...prev,
                        activeSession: prev.activeSession ? { ...prev.activeSession, status: 'working' } : prev.activeSession,
                        activeBreak: null,
                        isLoading: false,
                        error: null,
                    }));
                    activeSessionPromise = null;
                    // Resume the timer
                    if (state.activeSession) {
                        startTimer(state.activeSession);
                    }
                    return true;
                }
                else {
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        error: response.error || 'Failed to cancel break',
                    }));
                    return false;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to cancel break';
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: errorMessage,
                }));
                return false;
            }
        }, [state.activeBreak, state.activeSession, startTimer, fetchOpenBreak]);
        // SSE integration for real-time updates
        const { connectionState: sseConnectionState, isConnected: isSSEConnected } = useWorkSessionSSE({
            baseUrl: sseBaseUrl,
            authToken: sseAuthToken,
            getAuthToken: sseGetAuthToken,
            enabled: enableSSE && !!sseBaseUrl,
            onSessionStarted: useCallback((event) => {
                // When we receive a session started event, refresh to get full session data
                // Session started event received
                refresh();
            }, [refresh]),
            onSessionEnded: useCallback((event) => {
                // When we receive a session ended event, clear the active session
                // Session ended event received
                setState((prev) => ({
                    ...prev,
                    activeSession: null,
                    activeBreak: null,
                }));
                stopTimer();
            }, [stopTimer]),
            onSessionUpdated: useCallback((event) => {
                // When session is updated, refresh to get latest data
                // Session updated event received
                refresh();
            }, [refresh]),
            onBreakStarted: useCallback((event) => {
                // When break starts, refresh to get break data
                // Break started event received
                refresh();
                // Stop timer during break
                stopTimer();
            }, [refresh, stopTimer]),
            onBreakEnded: useCallback((event) => {
                // When break ends, refresh and restart timer
                // Break ended event received
                refresh();
                // Timer will be restarted by refresh if session is active
            }, [refresh]),
        });
        useEffect(() => {
            if (autoInitialize && !initializingRef.current) {
                initializingRef.current = true;
                setState((prev) => ({ ...prev, isLoading: true }));
                refresh().finally(() => {
                    setState((prev) => ({ ...prev, isLoading: false }));
                });
            }
        }, [autoInitialize, refresh]);
        // Track the offline queue: refresh from the server once a replay drains
        // it so the widget converges on real state.
        useEffect(() => {
            if (!offlineQueue)
                return;
            return offlineQueue.subscribe(() => {
                const count = offlineQueue.pendingCount();
                setPendingPunches((prev) => {
                    if (prev > 0 && count === 0) {
                        refresh();
                    }
                    return count;
                });
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [offlineQueue]);
        // Set up polling interval. With SSE enabled, polling runs only as a
        // FALLBACK while the stream is disconnected — a connected SSE client
        // already receives every change, and mobile previously ran both,
        // making ~60x the /work-sessions/active calls for nothing.
        const pollingActive = enablePolling && !(enableSSE && isSSEConnected);
        useEffect(() => {
            if (!pollingActive || !state.isInitialized)
                return;
            const pollInterval = setInterval(() => {
                // A refused session (mobile, see sessionLatch) cannot be polled back
                // to life: skip the tick until a new sign-in.
                if (isSessionDead())
                    return;
                refresh();
            }, refreshInterval);
            return () => {
                clearInterval(pollInterval);
            };
        }, [pollingActive, refreshInterval, state.isInitialized, refresh]);
        useEffect(() => {
            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        }, []);
        return {
            ...state,
            isClockedIn: !!state.activeSession,
            // The server's session status is the backstop: if it says on_break the
            // user IS on a break, even in the window before the break row has been
            // rehydrated. Showing "working" there is the bug this guards against.
            isOnBreak: !!state.activeBreak || state.activeSession?.status === 'on_break',
            activeBreak: state.activeBreak,
            elapsedMs,
            clockIn,
            clockOut,
            startBreak,
            endBreak,
            cancelBreak,
            refresh,
            clearError,
            openElsewhere,
            dismissOpenElsewhere,
            noLocationAssigned,
            dismissNoLocationAssigned,
            // SSE connection state (only populated when enableSSE is true)
            sseConnectionState: enableSSE ? sseConnectionState : undefined,
            isSSEConnected: enableSSE ? isSSEConnected : undefined,
            // Offline punches waiting for replay (0 when no queue is wired)
            pendingPunchCount: pendingPunches,
        };
    }
    // The 2s active-session cache is factory-scoped and shared by every
    // consumer of this binding — tests must reset it between cases or the
    // previous test's session leaks into the next.
    useWorkSession.resetSessionCacheForTests = () => {
        activeSessionPromise = null;
    };
    return useWorkSession;
}
